import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TooltipProvider } from '@/components/ui/tooltip';
import {
  Save,
  Loader2,
  Eye,
  EyeOff,
  RefreshCw,
  Cpu,
  Key,
  Mail,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Beaker,
  Plus,
  Trash2,
  BookOpen,
  Terminal,
  Sparkles,
  Orbit,
} from 'lucide-react';
import { toast } from 'sonner';

import {
  brainConfigService,
  type BrainSettingsPayload,
  type DomainKnowledgeTemplateSummary,
  type LlmModelSummary,
  type ProviderKeyDescriptor,
  type ProviderSlug,
  type LlmRole,
  type TestProviderKeyResult,
  type ActiveLlmConfig,
} from '@/services/kbService';
import RefreshButton from '@/components/knowledge/RefreshButton';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { logger } from '@/utils/logger';
import OutgoingEmailSection from '@/components/admin/company/email/OutgoingEmailSection';
import LlmModelDialog from '@/components/admin/company/llm/LlmModelDialog';
import { notifySetupStatusChanged } from '@/utils/setupStatus';

// ----------------------------------------------------------------------------
// Static metadata
// ----------------------------------------------------------------------------

interface ProviderMeta {
  slug: ProviderSlug;
  label: string;
  blurb: string;
  // Default model suggestions surfaced as a placeholder; the field stays
  // free-text so an operator can plug in a newer model name the day it
  // ships, without waiting for a frontend deploy.
  defaultModel: string;
  // Icon for the provider tile in the API Infrastructure section.
  icon: React.ComponentType<{ className?: string }>;
}

const PROVIDERS: ProviderMeta[] = [
  { slug: 'openai',    label: 'OpenAI',    blurb: 'GPT family',                defaultModel: 'gpt-4o-mini', icon: Terminal },
  { slug: 'anthropic', label: 'Anthropic', blurb: 'Claude family',             defaultModel: 'claude-haiku-4-5-20251001', icon: Sparkles },
  { slug: 'gemini',    label: 'Gemini',    blurb: 'Google Generative AI',      defaultModel: 'gemini-2.5-flash', icon: Orbit },
];

// Resolve a provider slug to its display label (e.g. "openai" -> "OpenAI")
// for user-facing messages. Falls back to the raw slug if unknown.
const providerLabel = (slug: ProviderSlug): string =>
  PROVIDERS.find((p) => p.slug === slug)?.label || slug;

// Per-role row config. The provider list narrows for roles that the
// extraction pipeline doesn't support cleanly on Gemini yet (the joint
// function-calling path is OpenAI-specific) - mirrors the Select
// options on the backend doctype.
interface RoleMeta {
  role: LlmRole;
  label: string;
  description: string;
  providers: ProviderSlug[];
  providerField: keyof BrainSettingsPayload;
  modelField: keyof BrainSettingsPayload;
  // Additional backend field pairs kept in lockstep with the primary
  // provider/model. Used when one UI control governs several backend
  // roles - e.g. the merged "Chat / Chat Title" row writes the same
  // provider+model to both the chat and chat-title fields.
  mirrorProviderFields?: (keyof BrainSettingsPayload)[];
  mirrorModelFields?: (keyof BrainSettingsPayload)[];
  // Only render this row for System Managers. Admins can open the panel
  // but must not see/edit this role (enforced server-side too - see
  // brain_settings_api._SYSTEM_MANAGER_ONLY_FIELDS).
  systemManagerOnly?: boolean;
}

const ROLES: RoleMeta[] = [
  {
    role: 'extraction',
    label: 'Entity Extraction',
    description: 'Pulls entities + properties out of ingested documents.',
    providers: ['openai', 'anthropic'],
    providerField: 'llm_extraction_provider',
    modelField: 'llm_extraction_model',
  },
  {
    role: 'chat',
    label: 'Chat / Chat Title',
    description:
      'Answers user questions over the knowledge base and generates short titles for new chat sessions.',
    providers: ['openai', 'anthropic', 'gemini'],
    providerField: 'chat_provider',
    modelField: 'chat_model',
    // Chat title generation shares the chat provider+model - one control
    // drives both backend field pairs.
    mirrorProviderFields: ['chat_title_provider'],
    mirrorModelFields: ['chat_title_model'],
  },
  {
    role: 'agent',
    label: 'Agent Base',
    description: 'Powers the agent surface (document / entity / voice agents).',
    providers: ['openai', 'anthropic', 'gemini'],
    providerField: 'agent_provider',
    modelField: 'agent_model',
    systemManagerOnly: true,
  },
  {
    role: 'relationship',
    label: 'Relationship Extraction',
    description: 'Joins entities into relationships across the graph.',
    providers: ['openai', 'anthropic'],
    providerField: 'relationship_extraction_provider',
    modelField: 'relationship_extraction_model',
  },
  {
    role: 'eval',
    label: 'Evaluation / Grading',
    description: 'Background grader and deep-document analysis.',
    providers: ['openai', 'anthropic', 'gemini'],
    providerField: 'eval_provider',
    modelField: 'eval_model',
  },
];

// Micro label used across the dark My Company revamp (matches
// CompanyBrandingPage.FIELD_LABEL_CLASS).
const MICRO_LABEL =
  'text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground';

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

function keySourceLabel(source: ProviderKeyDescriptor['source']): string {
  switch (source) {
    case 'llm_provider':    return 'KB Settings';
    case 'site_config':     return 'site_config.json';
    case 'gemini_settings': return 'Gemini Settings (legacy)';
    case 'env':             return 'environment variable';
    default:                return 'unset';
  }
}

function frappeErrorMessage(err: unknown, fallback: string): string {
  // Frappe surfaces validation errors via the response body's
  // `_server_messages` JSON-encoded array. Pull the first one if
  // present, otherwise fall back to the generic message.
  if (
    err &&
    typeof err === 'object' &&
    'response' in err &&
    err.response &&
    typeof err.response === 'object'
  ) {
    const response = (err as { response?: { data?: { _server_messages?: string; exception?: string } } }).response;
    const data = response?.data;
    if (data?._server_messages) {
      try {
        const messages: string[] = JSON.parse(data._server_messages);
        if (messages.length > 0) {
          const parsed = JSON.parse(messages[0]) as { message?: string };
          if (parsed.message) return parsed.message;
        }
      } catch {
        // fall through
      }
    }
    if (data?.exception) return data.exception;
  }
  return fallback;
}

// Section header used by every block on the revamped page - icon + title +
// one-line subtitle, matching the approved dark design.
const SectionHeader: React.FC<{
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  subtitle: string;
  action?: React.ReactNode;
}> = ({ icon: Icon, title, subtitle, action }) => (
  <div className="flex items-start justify-between gap-3">
    <div>
      <div className="flex items-center gap-2.5">
        <Icon className="h-5 w-5 text-primary" />
        <h2 className="text-xl font-semibold tracking-tight text-foreground">{title}</h2>
      </div>
      <p className="text-sm text-muted-foreground mt-1.5">{subtitle}</p>
    </div>
    {action}
  </div>
);

// ----------------------------------------------------------------------------
// Page
// ----------------------------------------------------------------------------

/**
 * KBSettingsPanel
 *
 * Embeddable KB Settings UI - renders the stacked configurator sections
 * (AI Context / API Infrastructure / Model Routing / Email Configuration)
 * without any page-level chrome. Lives inside `CompanyBrandingPage` below
 * the Organization Profile section; can be re-used wherever an inline
 * admin surface is wanted.
 *
 * Backed by the `Orgain Brain Settings` Single doctype (Frappe-side
 * name kept as "Brain Settings" - the user-facing label is now
 * "KB Settings", but the doctype + API module names are unchanged
 * to avoid an unnecessary database migration).
 *
 * The panel manages its OWN dirty state + save lifecycle (separate
 * from any react-hook-form the parent page may be using for the
 * branding form), so saving one half does not touch the other.
 */
interface KBSettingsPanelProps {
  // Admins can open the panel, but System-Manager-only rows (e.g. Agent
  // Base routing) are hidden unless this is true. Defaults to false so a
  // caller that forgets to pass it gets the more restrictive view.
  isSystemManager?: boolean;
}

const KBSettingsPanel: React.FC<KBSettingsPanelProps> = ({ isSystemManager = false }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const [settings, setSettings] = useState<BrainSettingsPayload | null>(null);
  const [draft, setDraft] = useState<BrainSettingsPayload | null>(null);
  const [industries, setIndustries] = useState<DomainKnowledgeTemplateSummary[]>([]);
  const [activeConfig, setActiveConfig] = useState<ActiveLlmConfig | null>(null);
  // Available LLM Models grouped by lowercased provider name
  // ("openai" / "anthropic" / "google" / etc.). Populated once on mount
  // from the LLM Model doctype so admins can add/remove models without
  // touching code - the model picker reads from this map.
  const [modelsByProvider, setModelsByProvider] = useState<Record<string, LlmModelSummary[]>>({});

  // Per-provider local state for the API-key inputs. Plaintext stays
  // strictly in component memory and is cleared on success - the
  // backend never returns plaintext on subsequent reads.
  const [keyDrafts, setKeyDrafts] = useState<Record<ProviderSlug, string>>({
    openai: '',
    anthropic: '',
    gemini: '',
  });
  const [keyVisible, setKeyVisible] = useState<Record<ProviderSlug, boolean>>({
    openai: false,
    anthropic: false,
    gemini: false,
  });
  const [keyBusy, setKeyBusy] = useState<Record<ProviderSlug, boolean>>({
    openai: false,
    anthropic: false,
    gemini: false,
  });
  const [keyTest, setKeyTest] = useState<Partial<Record<ProviderSlug, TestProviderKeyResult>>>({});

  // Native "Add LLM Model" dialog - replaces the Frappe desk deep-link.
  const [addModelOpen, setAddModelOpen] = useState(false);
  const [addModelProviderKey, setAddModelProviderKey] = useState<string>('');

  const loadAll = useCallback(async () => {
    try {
      const [payload, templates, config, models] = await Promise.all([
        brainConfigService.get(),
        brainConfigService.listIndustries(true),
        brainConfigService.describeActiveConfig().catch(() => null),
        // Pull every enabled LLM Model up-front (one round-trip); we
        // index by lowercased provider name so the per-row Select can
        // filter locally as the provider picker changes.
        brainConfigService.listLlmModels(undefined, true).catch(() => [] as LlmModelSummary[]),
      ]);
      setSettings(payload);
      setDraft(payload);
      setIndustries(templates);
      setActiveConfig(config);
      const grouped: Record<string, LlmModelSummary[]> = {};
      for (const m of models) {
        const key = (m.provider || '').toLowerCase();
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(m);
      }
      setModelsByProvider(grouped);
    } catch (err) {
      logger.error('Failed to load brain settings', err);
      toast.error(frappeErrorMessage(err, 'Failed to load KB Settings'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const handleRefresh = () => {
    setRefreshing(true);
    void loadAll();
  };

  const updateDraft = (patch: Partial<BrainSettingsPayload>) => {
    setDraft((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  // The Email section persists its fields immediately (sender selection,
  // notifications toggle, clearing a deleted account) rather than through the
  // panel's Save bar - account CRUD is already committed server-side, so these
  // should feel inline too. We patch only the email fields and advance BOTH
  // the draft and the saved baseline, so the panel never goes "dirty" from the
  // Email section while preserving any unsaved Industry/LLM edits.
  //
  // All email writes target the SAME "Orgain Brain Settings" Single, and the
  // section can fire several in quick succession (auto-default sender, delete →
  // clear, notifications toggle). Overlapping requests race on the Single in
  // Postgres (SerializationFailure on tabSingles → 500 → a spurious "Could not
  // save email settings" toast that surfaces wherever you've scrolled to).
  // We chain every write through this ref so only one is ever in flight.
  const emailWriteChain = useRef<Promise<unknown>>(Promise.resolve());
  const commitEmail = useCallback(
    async (
      patch: Partial<BrainSettingsPayload>,
      opts?: { silent?: boolean },
    ) => {
      const run = emailWriteChain.current.then(async () => {
        const keys = Object.keys(patch);
        let prevValues: Record<string, unknown> = {};
        // Optimistically advance draft + baseline so there's no dirty flicker.
        setSettings((prev) => {
          if (!prev) return prev;
          prevValues = Object.fromEntries(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            keys.map((k) => [k, (prev as any)[k]]),
          );
          return { ...prev, ...patch };
        });
        setDraft((prev) => (prev ? { ...prev, ...patch } : prev));
        try {
          await brainConfigService.update(patch as BrainSettingsPayload);
        } catch (err) {
          logger.error('Failed to save email settings', err);
          // The auto-default sender write is a background side-effect the
          // user never triggered - failing it loudly (and rolling back into
          // a re-fire) is what produced the "random" toast. Stay silent for
          // those; only surface failures the user actually initiated.
          if (!opts?.silent) {
            toast.error(
              frappeErrorMessage(err, 'Could not save email settings'),
            );
          }
          // Roll back just the patched fields on both sides.
          setSettings((prev) => (prev ? { ...prev, ...prevValues } : prev));
          setDraft((prev) => (prev ? { ...prev, ...prevValues } : prev));
        }
      });
      // Keep the chain alive even if this link rejects, so a failed write
      // never wedges every subsequent email save.
      emailWriteChain.current = run.catch(() => {});
      return run;
    },
    [],
  );

  // A provider is only routable once its API key is configured - otherwise
  // the live request would fail with a missing-credential error. The routing
  // pickers filter on this so operators can't select a provider they haven't
  // keyed up yet.
  const isProviderConfigured = (slug: ProviderSlug): boolean =>
    !!settings?.provider_keys?.[slug]?.is_set;

  // Re-fetch the model registry and regroup by provider - called after the
  // native Add-Model dialog saves, so the routing pickers update in place.
  const refreshModels = useCallback(async () => {
    const models = await brainConfigService
      .listLlmModels(undefined, true)
      .catch(() => [] as LlmModelSummary[]);
    const grouped: Record<string, LlmModelSummary[]> = {};
    for (const m of models) {
      const key = (m.provider || '').toLowerCase();
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(m);
    }
    setModelsByProvider(grouped);
  }, []);

  const isDirty = (() => {
    if (!settings || !draft) return false;
    // Avoid `Set` iteration here - tsconfig targets ES5 and we don't
    // want to flip on --downlevelIteration just for this one check.
    const seen: Record<string, true> = {};
    const allKeys: string[] = [];
    for (const k of Object.keys(settings)) {
      if (!seen[k]) { seen[k] = true; allKeys.push(k); }
    }
    for (const k of Object.keys(draft)) {
      if (!seen[k]) { seen[k] = true; allKeys.push(k); }
    }
    for (const k of allKeys) {
      if (k === 'provider_keys') continue;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((settings as any)[k] !== (draft as any)[k]) return true;
    }
    return false;
  })();

  const handleSave = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const next = await brainConfigService.update(draft);
      setSettings(next);
      setDraft(next);
      toast.success('KB Settings saved');
      notifySetupStatusChanged();
      // Refresh active config chip - provider/model bindings may have moved.
      const config = await brainConfigService.describeActiveConfig().catch(() => null);
      setActiveConfig(config);
    } catch (err) {
      logger.error('Failed to save brain settings', err);
      toast.error(frappeErrorMessage(err, 'Failed to save KB Settings'));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveKey = async (slug: ProviderSlug) => {
    const value = (keyDrafts[slug] || '').trim();
    if (!value) {
      toast.warning('Enter a key first, or use "Clear" to remove the stored key.');
      return;
    }
    setKeyBusy((prev) => ({ ...prev, [slug]: true }));
    try {
      const result = await brainConfigService.updateApiKey(slug, value);
      // Update the masked descriptor in-place so the UI flips to
      // "VERIFIED ··· last4" without a full refetch.
      setSettings((prev) =>
        prev
          ? {
              ...prev,
              provider_keys: { ...(prev.provider_keys || {}), [slug]: result.key } as BrainSettingsPayload['provider_keys'],
            }
          : prev,
      );
      setKeyDrafts((prev) => ({ ...prev, [slug]: '' }));
      setKeyTest((prev) => ({ ...prev, [slug]: undefined }));
      toast.success(`${providerLabel(slug)} API key saved`);
      notifySetupStatusChanged();
    } catch (err) {
      logger.error('Failed to save API key', err);
      toast.error(frappeErrorMessage(err, 'Failed to save API key'));
    } finally {
      setKeyBusy((prev) => ({ ...prev, [slug]: false }));
    }
  };

  const handleClearKey = async (slug: ProviderSlug) => {
    setKeyBusy((prev) => ({ ...prev, [slug]: true }));
    try {
      const result = await brainConfigService.updateApiKey(slug, '');
      setSettings((prev) =>
        prev
          ? {
              ...prev,
              provider_keys: { ...(prev.provider_keys || {}), [slug]: result.key } as BrainSettingsPayload['provider_keys'],
            }
          : prev,
      );
      setKeyTest((prev) => ({ ...prev, [slug]: undefined }));
      toast.success(`${providerLabel(slug)} API key cleared`);
      notifySetupStatusChanged();
    } catch (err) {
      logger.error('Failed to clear API key', err);
      toast.error(frappeErrorMessage(err, 'Failed to clear API key'));
    } finally {
      setKeyBusy((prev) => ({ ...prev, [slug]: false }));
    }
  };

  const handleTestKey = async (slug: ProviderSlug) => {
    setKeyBusy((prev) => ({ ...prev, [slug]: true }));
    try {
      const result = await brainConfigService.testApiKey(slug);
      setKeyTest((prev) => ({ ...prev, [slug]: result }));
      if (result.ok) {
        toast.success(
          `${providerLabel(slug)}: ${result.model || 'ok'} in ${result.latency_ms || '?'}ms`,
        );
      } else {
        toast.error(`${providerLabel(slug)}: ${result.error || 'test failed'}`);
      }
    } catch (err) {
      logger.error('Provider test failed', err);
      toast.error(frappeErrorMessage(err, 'Test failed'));
    } finally {
      setKeyBusy((prev) => ({ ...prev, [slug]: false }));
    }
  };

  // API Keys section is always available to KB-role users - the per-tenant
  // service-tier gating that used to hide it has been retired.
  const apiKeysVisible = true;

  if (loading || !draft) {
    // fullScreen={false} keeps the spinner inside the panel -
    // otherwise LoadingSpinner defaults to a fixed `inset-0` overlay
    // with a backdrop blur, which would cover the whole My Company
    // page instead of just this panel.
    return (
      <div className="min-h-[300px] flex items-center justify-center">
        <LoadingSpinner fullScreen={false} size="md" message="Loading KB Settings…" />
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-10">
        {/* AI CONTEXT ------------------------------------------------------
            Domain knowledge template + company-specific instructions. These
            fields save via this panel's sticky bar (Orgain Brain Settings),
            NOT the Organization Profile Save button above - the two save
            surfaces are deliberately separate. */}
        <section className="space-y-4">
          <SectionHeader
            icon={BookOpen}
            title="AI Context"
            subtitle="Tailor the AI to your industry and your organisation so every answer reflects your business."
            action={<RefreshButton onClick={handleRefresh} loading={refreshing} />}
          />
          <Card className="border-border bg-card/40">
            <CardContent className="p-6 sm:p-8 space-y-6">
              <div className="space-y-2 sm:max-w-md">
                <Label className={MICRO_LABEL}>Industry Template</Label>
                <Select
                  value={draft.domain_knowledge_template || ''}
                  onValueChange={(v) =>
                    updateDraft({ domain_knowledge_template: v || null })
                  }
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Please select the industry template" />
                  </SelectTrigger>
                  <SelectContent className="dark">
                    {industries.map((t) => (
                      <SelectItem key={t.name} value={t.name}>
                        {t.display_label || t.name}
                        {t.industry ? ` - ${t.industry}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Select the industry that best matches your organisation -
                  this determines which entity types the system extracts from
                  your documents. Takes effect on save.
                </p>
              </div>

              <div className="space-y-2">
                <Label className={MICRO_LABEL}>Company Context for AI</Label>
                <Textarea
                  value={draft.custom_domain_prompt || ''}
                  onChange={(e) =>
                    updateDraft({ custom_domain_prompt: e.target.value })
                  }
                  placeholder="Describe your company - what you do, your products and services, key terminology, and anything the AI should keep in mind when answering questions..."
                  rows={5}
                />
                <p className="text-xs text-muted-foreground">
                  This is added to the AI's instructions so responses reflect
                  your organisation. The more detail you provide, the more
                  tailored every answer becomes.
                </p>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* API INFRASTRUCTURE -------------------------------------------- */}
        {apiKeysVisible && (
          <section className="space-y-4">
            <SectionHeader
              icon={Key}
              title="AI Provider Keys"
              subtitle="Add your API keys for OpenAI, Anthropic, and Gemini. Keys are encrypted at rest."
            />
            <Card className="border-border bg-card/40">
              <CardContent className="p-2 sm:p-4 divide-y divide-border">
                {PROVIDERS.map((p) => {
                  const descriptor: ProviderKeyDescriptor = settings?.provider_keys?.[p.slug] || {
                    is_set: false,
                    last4: null,
                    source: null,
                  };
                  const visible = !!keyVisible[p.slug];
                  const busy = !!keyBusy[p.slug];
                  const draftKey = keyDrafts[p.slug] || '';
                  const testResult = keyTest[p.slug];
                  const ProviderIcon = p.icon;

                  return (
                    <div key={p.slug} className="p-4 space-y-3">
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
                        {/* Identity: icon tile + name + blurb + verified line */}
                        <div className="flex min-w-0 flex-1 items-center gap-4">
                          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-secondary/60 border border-border">
                            <ProviderIcon className="h-5 w-5 text-primary" />
                          </div>
                          <div className="min-w-0">
                            <div className="text-base font-semibold">{p.label}</div>
                            <p className="text-xs text-muted-foreground">{p.blurb}</p>
                            {descriptor.is_set ? (
                              <p className="mt-1 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.08em] text-[hsl(var(--success))]">
                                <CheckCircle2 className="h-3 w-3" />
                                Verified: ··· {descriptor.last4}
                              </p>
                            ) : (
                              <p className="mt-1 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
                                <XCircle className="h-3 w-3" />
                                Not configured
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Key input + actions */}
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                          <div className="relative flex items-center">
                            <Input
                              className="h-10 w-full sm:w-[240px] font-mono text-sm pr-9"
                              type={visible ? 'text' : 'password'}
                              value={draftKey}
                              onChange={(e) =>
                                setKeyDrafts((prev) => ({
                                  ...prev,
                                  [p.slug]: e.target.value,
                                }))
                              }
                              placeholder={descriptor.is_set ? 'Enter a new key to replace' : `${p.slug === 'gemini' ? 'AIzaSy' : p.slug === 'anthropic' ? 'sk-ant-' : 'sk-'}...`}
                              disabled={busy}
                            />
                            {/* Show/hide only reveals what you TYPE; the stored
                                key is write-only (never returned). So the toggle
                                is only useful while entering a new key - hide it
                                when the input is empty to avoid implying it can
                                reveal the saved key. */}
                            {draftKey && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="absolute right-0 h-10 w-9"
                                onClick={() =>
                                  setKeyVisible((prev) => ({
                                    ...prev,
                                    [p.slug]: !prev[p.slug],
                                  }))
                                }
                                aria-label={visible ? 'Hide key' : 'Show key'}
                              >
                                {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                              </Button>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              className="h-10"
                              onClick={() => handleTestKey(p.slug)}
                              disabled={busy || !descriptor.is_set}
                              title="Sends a 1-token ping to verify the stored key works"
                            >
                              <Beaker className="mr-1 h-4 w-4" />
                              Test Key
                            </Button>
                            <Button
                              type="button"
                              className="h-10"
                              onClick={() => handleSaveKey(p.slug)}
                              disabled={busy || !draftKey.trim()}
                            >
                              {busy ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <>
                                  <Save className="mr-1 h-4 w-4" />
                                  Save Key
                                </>
                              )}
                            </Button>
                            {descriptor.is_set && (
                              <Button
                                type="button"
                                variant="ghost"
                                className="h-10 text-destructive hover:text-destructive"
                                onClick={() => handleClearKey(p.slug)}
                                disabled={busy}
                              >
                                <Trash2 className="mr-1 h-4 w-4" />
                                Clear
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>

                      {descriptor.is_set && descriptor.source !== 'llm_provider' && descriptor.source !== null && (
                        <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-2 text-xs text-amber-200">
                          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                          <span>
                            A key is also configured in{' '}
                            <code>{keySourceLabel(descriptor.source)}</code>.
                            The factory uses the KB Settings key first,
                            so the value entered here will win on next save.
                          </span>
                        </div>
                      )}

                      {testResult && (
                        <div
                          className={`text-xs rounded-md p-2 ${
                            testResult.ok
                              ? 'bg-emerald-500/10 text-emerald-400'
                              : 'bg-rose-500/10 text-rose-400'
                          }`}
                        >
                          {testResult.ok ? (
                            <>
                              ✓ {testResult.model} responded in{' '}
                              {testResult.latency_ms}ms
                            </>
                          ) : (
                            <>✗ {testResult.error || 'test failed'}</>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </section>
        )}

        {/* MODEL ROUTING --------------------------------------------------- */}
        <section className="space-y-4">
          <SectionHeader
            icon={Cpu}
            title="Model Routing"
            subtitle="Assign specific LLM models to system capabilities based on performance requirements."
          />
          <Card className="border-border bg-card/40">
            <CardContent className="p-2 sm:p-4">
              {!PROVIDERS.some((p) => isProviderConfigured(p.slug)) && (
                <p className="flex items-center gap-1.5 p-4 text-sm font-medium text-destructive">
                  <AlertTriangle className="h-4 w-4" />
                  Add an API key in AI Provider Keys to start assigning
                  models to tasks.
                </p>
              )}

              {/* Column headers (desktop) */}
              <div className="hidden lg:grid lg:grid-cols-[1fr_220px_260px] gap-3 px-4 pt-2 pb-3 border-b border-border">
                <span className={MICRO_LABEL}>Capability</span>
                <span className={MICRO_LABEL}>Provider</span>
                <span className={MICRO_LABEL}>Model Selection</span>
              </div>

              <div className="divide-y divide-border">
                {ROLES.filter(
                  (row) => !row.systemManagerOnly || isSystemManager,
                ).map((row) => {
                  const provider = (draft[row.providerField] as ProviderSlug | null) || 'openai';
                  const model = (draft[row.modelField] as string | null) || '';
                  const active = activeConfig?.roles?.[row.role];
                  // Friendly labels for the "live" badge: the provider's
                  // display name + the registry model's model_name, falling
                  // back to the raw id when it isn't a managed record.
                  const activeProviderLabel = active?.provider
                    ? PROVIDERS.find((p) => p.slug === active.provider)?.label ||
                      active.provider
                    : '';
                  const activeModelLabel = (() => {
                    if (!active?.model) return '-';
                    const key =
                      active.provider === 'gemini'
                        ? 'google'
                        : (active.provider || '').toLowerCase();
                    const found = (modelsByProvider[key] || []).find(
                      (m) => m.name === active.model,
                    );
                    return found?.model_name || found?.name || active.model;
                  })();
                  // None of this role's allowed providers are keyed → the
                  // whole row is unusable, so disable both pickers.
                  const hasKeyedProvider = row.providers.some(isProviderConfigured);

                  return (
                    <div
                      key={row.role}
                      className="grid gap-3 p-4 lg:grid-cols-[1fr_220px_260px] lg:items-center"
                    >
                      <div>
                        <Label className="text-base font-semibold">{row.label}</Label>
                        <p className="text-xs text-muted-foreground mt-1">
                          {row.description}
                        </p>
                        {/* describe_active_config() resolves the role's
                            provider/model from the saved doctype fields
                            WITHOUT checking for a key, so it reports a
                            default (openai/gpt-4o-mini) even when unkeyed -
                            which would fail at request time. Only show the
                            "live" badge when that provider is actually
                            keyed. */}
                        {active &&
                          active.provider &&
                          isProviderConfigured(active.provider as ProviderSlug) && (
                            <Badge variant="secondary" className="mt-2 text-[10px]">
                              live: {activeProviderLabel} / {activeModelLabel}
                            </Badge>
                          )}
                      </div>
                      {/* Provider + Model: side-by-side on phone/tablet,
                          then dissolve (`lg:contents`) into the parent 3-col
                          grid at lg so the desktop row matches the design. */}
                      <div className="grid grid-cols-2 gap-3 lg:contents">
                        <div className="space-y-1">
                          <Label className={`${MICRO_LABEL} lg:hidden`}>
                            Provider
                          </Label>
                          <Select
                            disabled={!hasKeyedProvider}
                            // Fall back to empty (→ placeholder) when the
                            // stored provider has no key, so we don't render a
                            // selection the operator can't actually use.
                            value={isProviderConfigured(provider) ? provider : ''}
                            onValueChange={(v) => {
                              const slug = v as ProviderSlug;
                              // Default the model to the new provider's first
                              // registry model so the picker never lands on an
                              // empty "Select a model" after switching provider
                              // (the old model belongs to the previous provider
                              // and wouldn't be in this provider's registry).
                              const nextKey =
                                slug === 'gemini' ? 'google' : (slug || '').toLowerCase();
                              const firstModel =
                                (modelsByProvider[nextKey] || [])[0]?.name ?? null;
                              updateDraft({
                                [row.providerField]: slug,
                                [row.modelField]: firstModel,
                                ...Object.fromEntries(
                                  (row.mirrorProviderFields ?? []).map((f) => [
                                    f,
                                    slug,
                                  ]),
                                ),
                                ...Object.fromEntries(
                                  (row.mirrorModelFields ?? []).map((f) => [
                                    f,
                                    firstModel,
                                  ]),
                                ),
                              });
                            }}
                          >
                            <SelectTrigger className="h-10">
                              <SelectValue placeholder="No keyed providers" />
                            </SelectTrigger>
                            <SelectContent className="dark">
                              {/* Only keyed providers are selectable - an
                                  unkeyed one would fail the live request. The
                                  Select is disabled outright when none are
                                  keyed, so this list is always non-empty. */}
                              {row.providers
                                .filter(isProviderConfigured)
                                .map((slug) => (
                                  <SelectItem key={slug} value={slug}>
                                    {providerLabel(slug)}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className={`${MICRO_LABEL} lg:hidden`}>
                            Model
                          </Label>
                          {(() => {
                            // No keyed provider selected → disable the model
                            // picker, since we'd otherwise surface models for a
                            // provider the operator can't actually route to.
                            if (!isProviderConfigured(provider)) {
                              return (
                                <Select disabled value="">
                                  <SelectTrigger className="h-10">
                                    <SelectValue placeholder="Select a keyed provider first" />
                                  </SelectTrigger>
                                </Select>
                              );
                            }

                            // React provider slug → lowercased LLM Provider
                            // record key (used as the modelsByProvider index).
                            // The slug → record-name mapping mirrors the
                            // backend's _PROVIDER_RECORD_BY_SLUG:
                            //   openai → OpenAI, anthropic → Anthropic,
                            //   gemini → Google.
                            const providerKey =
                              provider === 'gemini'
                                ? 'google'
                                : (provider || '').toLowerCase();
                            const availableModels = modelsByProvider[providerKey] || [];

                            // Only registry models are selectable. If the saved
                            // value isn't one of them, treat the picker as empty
                            // so it shows the placeholder and prompts a re-pick
                            // (the stored value is left untouched until then).
                            const valueInRegistry =
                              !!model && availableModels.some((m) => m.name === model);

                            if (availableModels.length === 0) {
                              // Empty registry for this provider - offer the
                              // native Add-Model dialog (no desk exposure).
                              return (
                                <div className="flex items-center justify-between gap-2 p-2 border border-border rounded-md">
                                  <span className="text-xs text-muted-foreground italic">
                                    No models configured for this provider yet.
                                  </span>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      setAddModelProviderKey(providerKey);
                                      setAddModelOpen(true);
                                    }}
                                  >
                                    <Plus className="mr-1 h-3 w-3" />
                                    Add model
                                  </Button>
                                </div>
                              );
                            }

                            return (
                              <Select
                                value={valueInRegistry ? model : ''}
                                onValueChange={(v) =>
                                  updateDraft({
                                    [row.modelField]: v || null,
                                    ...Object.fromEntries(
                                      (row.mirrorModelFields ?? []).map((f) => [
                                        f,
                                        v || null,
                                      ]),
                                    ),
                                  })
                                }
                              >
                                <SelectTrigger className="h-10">
                                  <SelectValue placeholder="Select a model" />
                                </SelectTrigger>
                                <SelectContent className="dark">
                                  {availableModels.map((m) => (
                                    <SelectItem key={m.name} value={m.name}>
                                      {m.model_name || m.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Audio / video transcription was previously surfaced
                    here but has been removed from the UI: the backend
                    `audio_video_model` field on Orgain Brain Settings
                    is NOT factory-routed (transcription is hard-pinned
                    to Gemini in audio_tasks.py / video_tasks.py), so
                    exposing it as if it were a peer of the other roles
                    misled operators. The field stays on the doctype so
                    any background code that already reads it keeps
                    working; re-surface here once a real
                    `transcription_provider` is wired through the
                    factory. */}
              </div>
            </CardContent>
          </Card>
        </section>

        {/* EMAIL CONFIGURATION --------------------------------------------- */}
        <section className="space-y-4">
          <SectionHeader
            icon={Mail}
            title="Email Configuration"
            subtitle="Connect the email account used to send system notifications."
          />
          <Card className="border-border bg-card/40">
            <CardContent className="p-6 sm:p-8">
              <OutgoingEmailSection
                enableNotifications={!!draft.enable_email_notifications}
                emailAccount={draft.email_account || null}
                onChange={commitEmail}
              />
            </CardContent>
          </Card>
        </section>

        {/* Sticky save bar for dirty-form discoverability */}
        {isDirty && (
          <div className="sticky bottom-0 left-0 right-0 bg-background border-t border-border p-3 flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              <RefreshCw className="inline h-3 w-3 mr-1" />
              Unsaved changes.
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                onClick={() => setDraft(settings)}
                disabled={saving}
              >
                Discard
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save changes
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>

      <LlmModelDialog
        open={addModelOpen}
        onOpenChange={setAddModelOpen}
        defaultProviderKey={addModelProviderKey}
        onSaved={() => {
          void refreshModels();
        }}
      />
    </TooltipProvider>
  );
};

export default KBSettingsPanel;
